import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { TrinitiDatePicker, TrinitiTimePicker } from '@/components/triniti-datetime';
import { Clock } from 'lucide-react';
import { editErrorMessage } from './shiftEditRules';

/** SIL house shift: change the date and start / end time of this occurrence only. Residents, services, ratio and workers stay on the house view. */
export function SilShiftTimeDialog({ shift, open, onClose, onSaved }) {
  const [date, setDate] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && shift) { setDate(shift.date || ''); setStart(shift.start_time || ''); setEnd(shift.end_time || ''); setError(''); }
  }, [open, shift]);

  const save = async () => {
    if (!date || !start || !end) { setError('Choose a date, start and end time'); return; }
    if (start === end) { setError('Start and end time cannot be the same'); return; }
    const payload = {};
    if (date !== shift.date) payload.shift_date = date;
    if (start !== shift.start_time) payload.start_time = start;
    if (end !== shift.end_time) payload.end_time = end;
    if (!Object.keys(payload).length) { onClose(); return; }
    setSaving(true); setError('');
    try {
      await api.put(`/roster/shifts/${shift.id}/edit`, payload);
    } catch (e) {
      setError(editErrorMessage(e)); setSaving(false);
      return;
    }
    toast.success(payload.shift_date ? 'Shift moved' : 'Shift time updated');
    onSaved?.();
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="sil-time-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Clock className="w-4 h-4" /> Change date and time</DialogTitle>
          <DialogDescription>
            {shift?.house_name || 'SIL house'} · this occurrence only. Resident services that cover the whole shift move with it, and a new date is re-checked
            against every resident's agreement. Residents, services and workers are changed on the house view.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1 min-w-0"><Label className="text-xs">Date</Label><TrinitiDatePicker value={date} onChange={setDate} testId="sil-time-date" /></div>
          <div className="space-y-1 min-w-0"><Label className="text-xs">Start</Label><TrinitiTimePicker value={start} onChange={setStart} testId="sil-time-start" /></div>
          <div className="space-y-1 min-w-0"><Label className="text-xs">End</Label><TrinitiTimePicker value={end} onChange={setEnd} testId="sil-time-end" /></div>
        </div>
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-700" data-testid="sil-time-error">{error}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="sil-time-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="sil-time-save">{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
